import { gql, useMutation, useQuery } from "@apollo/client";
import { useState, useContext, useEffect } from "react";
import {
  LoadingView,
  InvalidReasons,
  NotificationContext,
  ToggleSwitch
} from "@solsys/solsys-reactjs";
import SaveButton from "../elements/SaveButton";
import { useIdempotentCreate, fingerprintProperties, mutationInput } from "../../utils/idempotency";
import ServiceGroupDropDown from "../../dropdowns/ServiceGroupDropDown";
import ServiceSectionDropDown from "../../dropdowns/ServiceSectionDropDown";
import ServiceTypeDropDown from "../../dropdowns/ServiceTypeDropDown";
import ServiceUnitDropDown from "../../dropdowns/ServiceUnitDropDown";

const entityQuery = gql`
  query service_itemList($service_item_list_request: service_item_list_request) {
    service_item(service_item_list_request: $service_item_list_request) {
      edges {
        id
        name
        sequence
        service_section {
          id
          name
        }
        service_group {
          id
          name
        }
        service_type {
          id
          name
        }
        service_unit {
          id
          name
        }
        required
      }
      totalCount
    }
  }
`;

const entityMutation = gql`
  mutation service_itemMutate($service_item_mutation_request: MutationInput) {
    service_item(service_item_mutation_request: $service_item_mutation_request) {
      id
      isNew
    }
  }
`;

export default function ServiceItemEdit({ id: entityId, action, navigate }) {
  
  const isEdit = action === "edit";
  const { addNotification } = useContext(NotificationContext);
  // variables for entity query const
  let variables = {
    service_item_list_request: {
      filters: [
        {
          field: "id",
          operator: "=",
          value: String(entityId ?? 0),
        },
      ],
    },
  };

  const [name, setName] = useState('');
  const [sequence, setSequence] = useState();
  const [serviceSection, setServiceSection] = useState();
  const [serviceGroup, setServiceGroup] = useState();
  const [serviceType, setServiceType] = useState();
  const [serviceUnit, setServiceUnit] = useState();
  const [required, setRequired] = useState(1);
  const [invalidReasons, setInvalidReasons] = useState([]);
  const createProperties = [
    { key: "name", string: name },
    { key: "sequence", number: Number(sequence) },
    { key: "required", number: required ? 1 : 0 },
    { key: "service_section_id", number: serviceSection ? Number(serviceSection) : null },
    { key: "service_group_id", number: serviceGroup ? Number(serviceGroup) : null },
    { key: "service_type_id", number: serviceType ? Number(serviceType) : null },
    { key: "service_unit_id", number: serviceUnit ? Number(serviceUnit) : null },
  ];
  const { idempotencyKey, isSaving, trackSave } = useIdempotentCreate({
    enabled: !isEdit,
    fingerprint: fingerprintProperties(createProperties),
  });

  // fetches entity and sets up refetch
  const {
    data,
    loading: loadingEntity,
    refetch: refetchEntity,
  } = useQuery(entityQuery, {
    variables,
    fetchPolicy: "network-only",
    onCompleted: (localData) => {
      const listResponse = localData?.service_item;
      const list = listResponse?.edges ?? [];
      const service_item = list[0];
      setName(service_item.name);
      setSequence(service_item.sequence);
      setServiceSection(service_item?.service_section?.id ?? "");
      setServiceGroup(service_item?.service_group?.id ?? "");
      setServiceType(service_item?.service_type?.id ?? "");
      setServiceUnit(service_item.service_unit?.id ?? "");
      setRequired(service_item.required);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const validateForm = () => {
    const reasons = [];

    if (!name || name.trim() === "") reasons.push("Name is required.");
    if (sequence === undefined || sequence === null || sequence === "") reasons.push("Order (Sequence) is required.");
    if (isNaN(Number(sequence))) reasons.push("Order (Sequence) must be a valid number.");
    if (serviceSection === null || serviceSection === "") reasons.push("Section is required.");
    if (serviceGroup === null || serviceGroup === "") reasons.push("Group is required.");
    if (serviceType === null || serviceType === "") reasons.push("Type is required.");
    if (serviceUnit === null || serviceUnit === "") reasons.push("Unit is required.");
    
    setInvalidReasons(reasons);
    return reasons.length === 0;
  };

  // sets up ServiceItem mutation
  const [mutateFunction, mutateInfo] = useMutation(entityMutation, {
    variables: {
      service_item_mutation_request: mutationInput({
        id: isEdit ? entityId : undefined,
        properties: createProperties,
        idempotencyKey,
      }),
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
    onCompleted: (response, originalRequestVariables) => {
      addNotification("good", "Service Item saved successfully");
      let id = response?.service_item?.id;
      navigate({
        action: "edit",
        id,
        push: false,
      });
      refetchEntity();
    },
  });
  
  useEffect(() => {
    refetchEntity();
  }, [entityId])

  let loadingMutation = mutateInfo.loading;

  return loadingEntity ? (
    <LoadingView />
  ) : (
    <>
      <div className="page-header">
        <span className="page-header-title">
          {isEdit ? "Edit" : "Add"} Service Item Detail
        </span> 
      </div>

      <div className="add-edit">
        <div className="edit-section">
          <div className="edit-property">
            <label>Name *:</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="edit-property">
            <label>Order *:</label>
            <input value={sequence} onChange={(e) => setSequence(e.target.value)} />
          </div>
          <div className="edit-property">
            <label>Section *:</label>
            <ServiceSectionDropDown
              selected={serviceSection}
              onSelect={setServiceSection}
              searchEnabled
            />
          </div>
          <div className="edit-property">
            <label>Group *:</label>
            <ServiceGroupDropDown
              selected={serviceGroup}
              onSelect={setServiceGroup}
              searchEnabled
            />
          </div>
          <div className="edit-property">
            <label>Type *:</label>
            <ServiceTypeDropDown
              selected={serviceType}
              onSelect={setServiceType}
              searchEnabled
            />
          </div>
          <div className="edit-property">
            <label>Units of Measure *:</label>
            <ServiceUnitDropDown
              selected={serviceUnit}
              onSelect={setServiceUnit}
              searchEnabled
            />
          </div>
          <div className="edit-property">
            <label>Is Required:</label>
            <ToggleSwitch
              checked={required}
              onChange={() => setRequired(!required)}
              size={{
                width: 60,
                height: 32,
                knob: 24,
                padding: 4
              }}
            />
          </div>
        </div>
        {invalidReasons.length > 0 && (
          <InvalidReasons reasons={invalidReasons} />
        )}
        <div className="edit-section">
          <SaveButton
            saving={!isEdit && isSaving}
            disabled={isEdit && loadingMutation}
            onClick={() => {
              if (!validateForm()) return;
              if (isEdit) mutateFunction();
              else trackSave(() => mutateFunction());
            }}
          />
        </div>
      </div>
    </>
  );
}
